import { useEffect, useMemo, useState } from 'react'
import { UserPlus, X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../ui/Modal'
import { formatCents } from '../../lib/money'
import type { OpportunityAssignmentDto, UserListItem } from '@toga/shared'

interface ProjectRoleOption {
  id: string
  name: string
  countsAsCAC: boolean
  active: boolean
}

interface UserCapacity {
  projectRoleId: string
  hourlyRateCents: number
  costPerHourCents: number
}

interface Props {
  opportunityId: string
  canManage: boolean
  assignments: OpportunityAssignmentDto[]
  onChanged: () => void
}

export default function AssignmentsPanel({
  opportunityId,
  canManage,
  assignments,
  onChanged,
}: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [adding, setAdding] = useState(false)

  const onRemove = async (a: OpportunityAssignmentDto) => {
    if (
      !(await confirmDialog({
        message: t('assignments.confirmRemove', {
          user: a.userFullName,
          role: a.projectRoleName,
        }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/opportunities/${opportunityId}/assignments/by-id/${a.id}`)
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        toast.error(body?.error ?? err.message)
      } else {
        toast.error(t('common:errors.network'))
      }
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          {t('assignments.team')}
        </h3>
        {canManage && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-700 dark:text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <UserPlus size={14} /> {t('assignments.add')}
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        <p className="t-muted text-sm">{t('assignments.empty')}</p>
      ) : (
        <ul className="space-y-1.5">
          {assignments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-hover/50"
            >
              <span className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-br from-toga-600 to-toga-800 text-white text-xs font-semibold">
                {a.userFullName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase() ?? '')
                  .join('')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="t-primary text-sm font-medium truncate">{a.userFullName}</div>
                <div className="t-muted text-xs flex items-center gap-2">
                  <span>{a.projectRoleName}</span>
                  {a.countsAsCAC && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                      {t('assignments.cacBadge')}
                    </span>
                  )}
                  <span>·</span>
                  <span>{a.hoursAllocated}h</span>
                  <span>·</span>
                  <span>{formatCents(a.hourlyRateCentsSnapshot)}/h</span>
                </div>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => onRemove(a)}
                  className="w-7 h-7 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                  title={t('assignments.remove')}
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddAssignmentModal
          opportunityId={opportunityId}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false)
            onChanged()
          }}
        />
      )}
    </div>
  )
}

function AddAssignmentModal({
  opportunityId,
  onClose,
  onAdded,
}: {
  opportunityId: string
  onClose: () => void
  onAdded: () => void
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [projectRoles, setProjectRoles] = useState<ProjectRoleOption[] | null>(null)
  const [capacities, setCapacities] = useState<UserCapacity[]>([])
  const [userId, setUserId] = useState('')
  const [projectRoleId, setProjectRoleId] = useState('')
  const [hoursAllocated, setHoursAllocated] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const [{ users: u }, { items: r }] = await Promise.all([
          api.get<{ users: UserListItem[] }>('/users'),
          api.get<{ items: ProjectRoleOption[] }>('/project-roles'),
        ])
        const available = u.filter((x) => x.isActive && x.role !== 'LECTURA')
        setUsers(available)
        setProjectRoles(r.filter((x) => x.active))
        if (available[0]) setUserId(available[0].id)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('assignments.loadError'))
      }
    })()
  }, [t])

  useEffect(() => {
    if (!userId) {
      setCapacities([])
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { items } = await api.get<{ items: UserCapacity[] }>(
          `/users/${userId}/project-roles`
        )
        if (cancelled) return
        setCapacities(items)
        if (items[0]) setProjectRoleId(items[0].projectRoleId)
        else setProjectRoleId('')
      } catch {
        if (!cancelled) setCapacities([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  // Filtramos las capacidades a project-roles ACTIVOS. Si un usuario tiene
  // capacidad para un rol que el admin desactivó después, no debe poder
  // seleccionarse aquí (rompería el contrato del backend de "rol activo").
  const usableCapacities = useMemo(() => {
    if (!projectRoles) return capacities
    const activeIds = new Set(projectRoles.map((r) => r.id))
    return capacities.filter((c) => activeIds.has(c.projectRoleId))
  }, [capacities, projectRoles])

  const capacity = useMemo(
    () => usableCapacities.find((c) => c.projectRoleId === projectRoleId) ?? null,
    [usableCapacities, projectRoleId]
  )

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.post(`/opportunities/${opportunityId}/assignments`, {
        userId,
        projectRoleId,
        hoursAllocated,
      })
      onAdded()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('common:errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const noCapacities = users && users.length > 0 && capacities.length === 0 && userId
  const onlyInactive =
    users && users.length > 0 && capacities.length > 0 && usableCapacities.length === 0 && userId

  return (
    <Modal open onClose={onClose} title={t('assignments.modalTitle')}>
      {users === null || projectRoles === null ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('common:loading')}
        </div>
      ) : users.length === 0 ? (
        <p className="t-muted text-sm">{t('assignments.noUsers')}</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('assignments.userLabel')}</label>
            <select
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">
              {t('assignments.projectRoleLabel')}
            </label>
            <select
              required
              value={projectRoleId}
              onChange={(e) => setProjectRoleId(e.target.value)}
              disabled={usableCapacities.length === 0}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary disabled:opacity-50"
            >
              {usableCapacities.length === 0 ? (
                <option value="">{t('assignments.noCapacitiesOption')}</option>
              ) : (
                usableCapacities.map((c) => {
                  const role = projectRoles.find((r) => r.id === c.projectRoleId)
                  if (!role) return null
                  return (
                    <option key={c.projectRoleId} value={c.projectRoleId}>
                      {role.name} · {formatCents(c.hourlyRateCents)}/h
                    </option>
                  )
                })
              )}
            </select>
            {noCapacities && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1.5">
                {t('assignments.noCapacitiesHint')}
              </p>
            )}
            {onlyInactive && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1.5">
                {t('assignments.onlyInactiveHint')}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">
              {t('assignments.hoursLabel')}
            </label>
            <input
              type="number"
              min={0}
              max={10000}
              step={1}
              value={hoursAllocated}
              onChange={(e) => setHoursAllocated(Number.parseInt(e.target.value || '0', 10))}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary"
            />
            {capacity && hoursAllocated > 0 && (
              <p className="text-xs t-muted mt-1.5">
                {t('assignments.estimatedCost')}{' '}
                <span className="text-toga-700 dark:text-toga-200 font-medium">
                  {formatCents(hoursAllocated * capacity.costPerHourCents)}
                </span>
                {' · '}
                {t('assignments.billable')}{' '}
                <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                  {formatCents(hoursAllocated * capacity.hourlyRateCents)}
                </span>
              </p>
            )}
          </div>
          {error && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || !projectRoleId}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {t('assignments.assign')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
